import { useEffect, useId, useRef, useState, type ReactNode, type InputHTMLAttributes, type ButtonHTMLAttributes } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2, X } from 'lucide-react';
import { ROLE_LABELS, type TeamRole } from '@hoopify/shared';

export function Spinner({ className = 'size-4' }: { className?: string }) {
  return <Loader2 className={`animate-spin ${className}`} aria-hidden />;
}

/**
 * Primary is the gradient hero action: use one per screen. `size="lg"` is the
 * full 52px button for sign-in pages and the main call to action.
 */
export function Button({
  variant = 'primary',
  size,
  loading,
  children,
  disabled,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'lg'; loading?: boolean }) {
  const base = className && /\bbtn-(primary|secondary|ghost|danger)\b/.test(className) ? '' : `btn-${variant}`;
  return (
    <button className={[base, size === 'lg' ? 'btn-lg' : '', className ?? ''].filter(Boolean).join(' ')} disabled={disabled || loading} {...rest}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: ReactNode;
}

export function Field({ label, error, hint, id, ...rest }: FieldProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fid} className="label-caps block">
        {label}
      </label>
      <input id={fid} className="field-input" aria-invalid={!!error} aria-describedby={error ? `${fid}-err` : hint ? `${fid}-hint` : undefined} {...rest} />
      {error ? (
        <p id={`${fid}-err`} className="text-[13px] text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fid}-hint`} className="text-[13px] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'ok' | 'info' | 'warn'; children: ReactNode }) {
  const styles = {
    danger: 'bg-danger-soft text-danger',
    ok: 'bg-ok-soft text-ok',
    info: 'bg-action-soft text-action',
    warn: 'bg-warn-soft text-warn',
  }[tone];
  const Icon = tone === 'ok' ? CheckCircle2 : tone === 'danger' ? AlertCircle : Info;
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={`flex gap-2.5 rounded-md px-3 py-2.5 text-[14px] ${styles}`}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 text-ink/90 dark:text-ink">{children}</div>
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        <h1 className="text-[24px] leading-tight sm:text-[28px]">{title}</h1>
        {description && <p className="mt-1 max-w-[62ch] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </header>
  );
}

export function Section({ title, description, children, aside }: { title: string; description?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="border-t border-line py-6 first:border-t-0 first:pt-0">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[16px] font-semibold">{title}</h2>
          {description && <p className="mt-0.5 max-w-[62ch] text-[14px] text-muted">{description}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

const roleTone: Record<TeamRole, string> = {
  head_coach: 'bg-action text-action-ink',
  assistant_coach: 'bg-action-soft text-action',
  viewer: 'bg-sunken text-muted',
  player: 'bg-ok-soft text-ok',
  parent: 'bg-warn-soft text-warn',
};

export function RoleBadge({ role }: { role: TeamRole }) {
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-medium ${roleTone[role]}`}>{ROLE_LABELS[role]}</span>;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line-strong px-6 py-10 text-center">
      <p className="font-medium">{title}</p>
      {children && <p className="mx-auto mt-1 max-w-[46ch] text-[14px] text-muted">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingBlock() {
  return (
    <div className="flex items-center gap-2 py-10 text-muted" role="status">
      <Spinner /> Loading…
    </div>
  );
}

/** Native <dialog> for focus trapping and Escape handling. */
export function Dialog({ open, onClose, title, children, size = 'md' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; size?: 'md' | 'lg' | 'xl' }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      // React passes a nested dialog's close event up to this one; only react to our own.
      onClose={(e) => e.target === ref.current && onClose()}
      // Big editors don't close on a stray click outside; small dialogs do.
      onClick={(e) => size === 'md' && e.target === ref.current && onClose()}
      className={`m-auto rounded-lg border border-line bg-surface p-0 text-ink shadow-xl backdrop:bg-black/40 ${
        size === 'xl' ? 'max-h-[calc(100dvh-16px)] w-[min(1120px,calc(100vw-16px))] overflow-y-auto' : size === 'lg' ? 'w-[min(720px,calc(100vw-32px))]' : 'w-[min(480px,calc(100vw-32px))]'
      }`}
    >
      {open && (
        <div className={size === 'xl' ? 'p-4 sm:p-5' : 'p-5'}>
          <div className="mb-4 flex items-start justify-between gap-4">
            <h2 className="text-[17px] font-semibold">{title}</h2>
            <button className="btn-ghost -m-1.5 p-1.5" onClick={onClose} aria-label="Close">
              <X className="size-4" />
            </button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

/** Small dropdown menu of actions. Closes on outside click or Escape. */
export function Menu({ label, children, align = 'right' }: { label: ReactNode; children: (close: () => void) => ReactNode; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button className="btn-secondary" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu">
        {label}
      </button>
      {open && (
        <div role="menu" className={`absolute z-30 mt-1 min-w-[200px] rounded-lg border border-line bg-surface p-1 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ onClick, children, tone }: { onClick: () => void; children: ReactNode; tone?: 'danger' }) {
  return (
    <button role="menuitem" onClick={onClick} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[14px] hover:bg-sunken ${tone === 'danger' ? 'text-danger' : ''}`}>
      {children}
    </button>
  );
}

export function TextArea({ label, error, hint, id, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string; hint?: ReactNode }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fid} className="label-caps block">
        {label}
      </label>
      <textarea id={fid} className="field-input min-h-[80px]" aria-invalid={!!error} {...rest} />
      {error ? <p className="text-[13px] text-danger">{error}</p> : hint ? <p className="text-[13px] text-muted">{hint}</p> : null}
    </div>
  );
}
